import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import api from "../services/api";
import "../styles/teams.css";

export default function Teams() {
  // ==================== STATE ====================
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [districts, setDistricts] = useState([]);
  const [blocksMap, setBlocksMap] = useState({});
  const [gpsMap, setGpsMap] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState({});

  const [districtCoords, setDistrictCoords] = useState([]);
  const [blockCoords, setBlockCoords] = useState([]);
  const [gpCoords, setGpCoords] = useState([]);
  const [paymentMap, setPaymentMap] = useState({});

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [memberDetails, setMemberDetails] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editPreviews, setEditPreviews] = useState({
    aadhaar: null,
    pan: null,
    passbook: null,
    photo: null,
  });

  // Check if current user is ADMIN (only ADMIN can edit)
  const isAdmin = currentUser?.role === "ADMIN";

  // ==================== FETCH CURRENT USER ====================
  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const res = await api.get("/auth/me");
      setCurrentUser(res.data);
    } catch (err) {
      console.error("Failed to get current user, fallback to ADMIN", err);
      setCurrentUser({ role: "ADMIN" });
    }
  };

  // ==================== LOAD DATA AFTER USER IS KNOWN ====================
  useEffect(() => {
    if (currentUser) loadAllData();
  }, [currentUser]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [distRes, blockRes, gpRes, paymentRes, districtRes] = await Promise.all([
        api.get("/dashboard/users/by-role/DISTRICT_COORDINATOR"),
        api.get("/dashboard/users/by-role/BLOCK_COORDINATOR"),
        api.get("/dashboard/users/by-role/GP_COORDINATOR"),
        api.get("/users/with-payment-status"),
        api.get("/dashboard/district-member-counts"),
      ]);

      setDistrictCoords(distRes.data || []);
      setBlockCoords(blockRes.data || []);
      setGpCoords(gpRes.data || []);

      const paymentMapTmp = {};
      (paymentRes.data || []).forEach(u => {
        paymentMapTmp[u.id] = { status: u.paymentStatus, amount: u.paymentAmount };
      });
      setPaymentMap(paymentMapTmp);

      const allDistricts = districtRes.data || [];
      setDistricts(allDistricts);

      let districtsToFetch = allDistricts;
      
      // Role-based filtering
      if (currentUser.role === "STATE_COORDINATOR") {
        districtsToFetch = allDistricts;
      } else if (currentUser.role === "DISTRICT_COORDINATOR") {
        districtsToFetch = allDistricts.filter(d => d.districtId === currentUser.districtId);
      } else if (currentUser.role === "BLOCK_COORDINATOR" || currentUser.role === "GP_COORDINATOR") {
        districtsToFetch = [];
      }

      const newBlocksMap = {};
      const newGpsMap = {};

      for (const district of districtsToFetch) {
        const blocksRes = await api.get(`/blocks?districtId=${district.districtId}`);
        const blocks = blocksRes.data || [];
        newBlocksMap[district.districtId] = blocks;
        for (const block of blocks) {
          const gpsRes = await api.get(`/gps?blockId=${block.id}`);
          newGpsMap[block.id] = gpsRes.data || [];
        }
      }

      // Handle BLOCK_COORDINATOR special case
      if (currentUser.role === "BLOCK_COORDINATOR") {
        let parentDistrictId = null;
        for (const district of allDistricts) {
          const blocksRes = await api.get(`/blocks?districtId=${district.districtId}`);
          const blocks = blocksRes.data || [];
          const found = blocks.find(b => b.id === currentUser.blockId);
          if (found) {
            parentDistrictId = district.districtId;
            newBlocksMap[parentDistrictId] = [found];
            break;
          }
        }
        if (parentDistrictId) {
          const gpsRes = await api.get(`/gps?blockId=${currentUser.blockId}`);
          newGpsMap[currentUser.blockId] = gpsRes.data || [];
        }
      }

      setBlocksMap(newBlocksMap);
      setGpsMap(newGpsMap);
    } catch (err) {
      console.error("Error loading teams data:", err);
    } finally {
      setLoading(false);
    }
  };

  // ==================== HELPER FUNCTIONS ====================
  const matchesSearch = (coord) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return coord.name?.toLowerCase().includes(term) || coord.email?.toLowerCase().includes(term);
  };

  const filteredDistrictCoords = districtCoords.filter(matchesSearch);
  const filteredBlockCoords = blockCoords.filter(matchesSearch);
  const filteredGpCoords = gpCoords.filter(matchesSearch);

  const getPaymentBadge = (userId) => {
    const payment = paymentMap[userId];
    if (!payment) return null;
    if (payment.status === "PAID") return <span className="payment-badge paid">✅ Paid</span>;
    if (payment.status === "PENDING") return <span className="payment-badge pending">⏳ Pending</span>;
    return <span className="payment-badge free">🆓 Free</span>;
  };

  const getDistrictName = (id) => {
    const d = districts.find(d => d.districtId === id);
    return d ? d.districtName : `District ${id}`;
  };
  
  const getBlockName = (districtId, blockId) => {
    const blocks = blocksMap[districtId] || [];
    const b = blocks.find(b => b.id === blockId);
    return b ? b.name : `Block ${blockId}`;
  };
  
  const getGpName = (blockId, gpId) => {
    const gps = gpsMap[blockId] || [];
    const g = gps.find(g => g.id === gpId);
    return g ? g.name : `GP ${gpId}`;
  };

  const toggleExpand = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const openMemberModal = async (member) => {
    setShowModal(true);
    setModalLoading(true);
    setActiveTab("basic");
    setIsEditing(false); // Always start in view mode
    try {
      const userRes = await api.get(`/users/${member.id}`);
      const userData = userRes.data;
      const payment = paymentMap[member.id];
      if (payment) {
        userData.paymentStatus = payment.status;
        userData.paymentAmount = payment.amount;
      }
      
      const docsRes = await api.get(`/users/documents/${member.id}`);
      const imageFields = {};
      
      if (docsRes.data && docsRes.data.length > 0) {
        docsRes.data.forEach(doc => {
          const typeLower = doc.type.toLowerCase();
          
          if (doc.data) {
            let base64String;
            
            if (typeof doc.data === 'string') {
              if (doc.data.startsWith('data:image')) {
                base64String = doc.data;
              } else {
                const fileType = doc.fileType || 'image/jpeg';
                base64String = `data:${fileType};base64,${doc.data}`;
              }
            } else {
              try {
                const bytes = new Uint8Array(doc.data);
                let binary = '';
                for (let i = 0; i < bytes.length; i++) {
                  binary += String.fromCharCode(bytes[i]);
                }
                const rawBase64 = btoa(binary);
                const fileType = doc.fileType || 'image/jpeg';
                base64String = `data:${fileType};base64,${rawBase64}`;
              } catch (err) {
                console.error(`Failed to convert ${typeLower} image:`, err);
                base64String = null;
              }
            }
            
            if (base64String) {
              imageFields[`${typeLower}Base64`] = base64String;
            }
          }
        });
      }
      
      const fullDetails = { ...userData, ...imageFields };
      setMemberDetails(fullDetails);
      setEditForm(fullDetails);
      setEditPreviews({ aadhaar: null, pan: null, passbook: null, photo: null });
    } catch (err) {
      console.error("Error loading member details:", err);
      setMemberDetails(member);
      setEditForm(member);
    }
    setModalLoading(false);
  };
  
  const closeModal = () => {
    setShowModal(false);
    setMemberDetails(null);
    setIsEditing(false);
    setEditForm({});
    setEditPreviews({ aadhaar: null, pan: null, passbook: null, photo: null });
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditFileChange = (type, file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setEditPreviews(prev => ({ ...prev, [type]: previewUrl }));
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setEditForm(prev => ({ ...prev, [`${type}Base64`]: reader.result }));
    };
  };

  const saveMember = async () => {
    if (!memberDetails?.id) return;
    
    setModalLoading(true);
    try {
      const documents = [];
      const imageTypes = ['aadhaar', 'pan', 'passbook', 'photo'];
      
      for (const type of imageTypes) {
        const imageBase64 = editForm[`${type}Base64`];
        if (imageBase64 && imageBase64.startsWith('data:')) {
          const base64Data = imageBase64.split(',')[1];
          const fileType = imageBase64.match(/data:([^;]+)/)[1];
          documents.push({
            type: type.toUpperCase(),
            data: base64Data,
            fileType: fileType
          });
        }
      }
      
      const payload = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        phone: editForm.phone,
        employeeId: editForm.employeeId,
        level: editForm.level,
        village: editForm.village,
        postOffice: editForm.postOffice,
        pin: editForm.pin,
        aadhaar: editForm.aadhaar,
        pan: editForm.pan,
        bankName: editForm.bankName,
        accountNumber: editForm.accountNumber,
        ifsc: editForm.ifsc,
        branch: editForm.branch,
        districtId: editForm.districtId,
        blockId: editForm.blockId,
        gpId: editForm.gpId,
        stateId: editForm.stateId,
        documents: documents
      };
      
      const response = await api.put(`/dashboard/${memberDetails.id}`, payload);
      
      alert("✅ Member updated successfully!");
      
      await loadAllData();
      setIsEditing(false);
      
      const updatedUserRes = await api.get(`/users/${memberDetails.id}`);
      const updatedDocsRes = await api.get(`/users/documents/${memberDetails.id}`);
      
      const updatedImageFields = {};
      if (updatedDocsRes.data && updatedDocsRes.data.length > 0) {
        updatedDocsRes.data.forEach(doc => {
          const typeLower = doc.type.toLowerCase();
          if (doc.data) {
            let base64String;
            if (typeof doc.data === 'string') {
              base64String = doc.data;
            } else {
              const bytes = new Uint8Array(doc.data);
              let binary = '';
              for (let i = 0; i < bytes.length; i++) {
                binary += String.fromCharCode(bytes[i]);
              }
              base64String = btoa(binary);
            }
            updatedImageFields[`${typeLower}Base64`] = `data:${doc.fileType || 'image/jpeg'};base64,${base64String}`;
          }
        });
      }
      
      const refreshed = { ...updatedUserRes.data, ...updatedImageFields };
      setMemberDetails(refreshed);
      setEditForm(refreshed);
      
    } catch (err) {
      console.error("ERROR IN SAVE:", err);
      alert("❌ Error: " + (err.response?.data?.message || err.message));
    } finally {
      setModalLoading(false);
    }
  };
  
  // ==================== RENDER MODAL ====================
  const renderModal = () => {
    if (!showModal) return null;
    
    return (
      <div className="modal-overlay" onClick={closeModal}>
        <div className="modal-container member-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-header-info">
              <div className="modal-avatar">👤</div>
              <div>
                <h3>{memberDetails?.name || "Member Details"}</h3>
                <p className="modal-role">{memberDetails?.role || "Coordinator"}</p>
              </div>
            </div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>
          <div className="modal-body">
            {modalLoading ? (
              <div className="loading-spinner">Loading...</div>
            ) : memberDetails ? (
              !isEditing ? (
                <div className="member-details-tabs">
                  <div className="tabs-header">
                    {["basic", "address", "bank", "hierarchy", "images"].map(tab => (
                      <button key={tab} className={`tab-btn ${activeTab === tab ? "active" : ""}`} onClick={() => setActiveTab(tab)}>
                        {tab === "basic" && "📋 Basic"}
                        {tab === "address" && "📍 Address"}
                        {tab === "bank" && "🏦 Bank"}
                        {tab === "hierarchy" && "🏢 Hierarchy"}
                        {tab === "images" && "🖼️ Images"}
                      </button>
                    ))}
                  </div>
                  <div className="tab-content">
                    {activeTab === "basic" && (
                      <div className="detail-grid">
                        <div className="detail-item"><label>Full Name</label><p>{memberDetails.name || "—"}</p></div>
                        <div className="detail-item"><label>Email</label><p>{memberDetails.email || "—"}</p></div>
                        <div className="detail-item"><label>Phone</label><p>{memberDetails.phone || "—"}</p></div>
                        <div className="detail-item"><label>Employee ID</label><p>{memberDetails.employeeId || "—"}</p></div>
                        <div className="detail-item"><label>Access Level</label><p>{memberDetails.level || "—"}</p></div>
                        <div className="detail-item"><label>Role</label><p>{memberDetails.role?.replace(/_/g, " ") || "—"}</p></div>
                        <div className="detail-item"><label>Payment Status</label><p>{getPaymentBadge(memberDetails.id) || "—"}</p></div>
                        <div className="detail-item"><label>Payment Amount</label><p>{memberDetails.paymentAmount ? `₹${memberDetails.paymentAmount}` : "—"}</p></div>
                      </div>
                    )}
                    {activeTab === "address" && (
                      <div className="detail-grid">
                        <div className="detail-item"><label>Village/Town</label><p>{memberDetails.village || "—"}</p></div>
                        <div className="detail-item"><label>Post Office</label><p>{memberDetails.postOffice || "—"}</p></div>
                        <div className="detail-item"><label>PIN Code</label><p>{memberDetails.pin || "—"}</p></div>
                        <div className="detail-item"><label>Aadhaar No.</label><p>{memberDetails.aadhaar || "—"}</p></div>
                        <div className="detail-item"><label>PAN No.</label><p>{memberDetails.pan || "—"}</p></div>
                      </div>
                    )}
                    {activeTab === "bank" && (
                      <div className="detail-grid">
                        <div className="detail-item"><label>Bank Name</label><p>{memberDetails.bankName || "—"}</p></div>
                        <div className="detail-item"><label>Account Number</label><p>{memberDetails.accountNumber || "—"}</p></div>
                        <div className="detail-item"><label>IFSC Code</label><p>{memberDetails.ifsc || "—"}</p></div>
                        <div className="detail-item"><label>Branch</label><p>{memberDetails.branch || "—"}</p></div>
                      </div>
                    )}
                    {activeTab === "hierarchy" && (
                      <div className="detail-grid">
                        <div className="detail-item"><label>District</label><p>{getDistrictName(memberDetails.districtId)}</p></div>
                        <div className="detail-item"><label>Block</label><p>{getBlockName(memberDetails.districtId, memberDetails.blockId)}</p></div>
                        <div className="detail-item"><label>Gram Panchayat</label><p>{getGpName(memberDetails.blockId, memberDetails.gpId)}</p></div>
                      </div>
                    )}
                    {activeTab === "images" && (
                      <div className="images-grid">
                        {["aadhaar", "pan", "passbook", "photo"].map(type => (
                          memberDetails[`${type}Base64`] ? (
                            <div key={type} className="image-card" onClick={() => setLightboxImage(memberDetails[`${type}Base64`])}>
                              <img src={memberDetails[`${type}Base64`]} alt={type} />
                              <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                            </div>
                          ) : (
                            <div key={type} className="image-card empty">No {type}</div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="edit-form-container">
                  <h3>Edit Member</h3>
                  <div className="detail-section">
                    <h4>Basic Information</h4>
                    <div className="detail-grid">
                      <div className="detail-item"><label>Name</label><input name="name" value={editForm.name || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>Email</label><input name="email" value={editForm.email || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>Phone</label><input name="phone" value={editForm.phone || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>Employee ID</label><input name="employeeId" value={editForm.employeeId || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>Access Level</label><input name="level" value={editForm.level || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item">
                        <label>Role</label>
                        <select name="role" value={editForm.role || ""} onChange={handleEditChange}>
                          <option value="">Select Role</option>
                          <option value="STATE_COORDINATOR">State Coordinator</option>
                          <option value="DISTRICT_COORDINATOR">District Coordinator</option>
                          <option value="BLOCK_COORDINATOR">Block Coordinator</option>
                          <option value="GP_COORDINATOR">GP Coordinator</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>Address</h4>
                    <div className="detail-grid">
                      <div className="detail-item"><label>Village/Town</label><input name="village" value={editForm.village || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>Post Office</label><input name="postOffice" value={editForm.postOffice || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>PIN Code</label><input name="pin" value={editForm.pin || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>Aadhaar</label><input name="aadhaar" value={editForm.aadhaar || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>PAN</label><input name="pan" value={editForm.pan || ""} onChange={handleEditChange} /></div>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>Bank Details</h4>
                    <div className="detail-grid">
                      <div className="detail-item"><label>Bank Name</label><input name="bankName" value={editForm.bankName || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>Account Number</label><input name="accountNumber" value={editForm.accountNumber || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>IFSC Code</label><input name="ifsc" value={editForm.ifsc || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>Branch</label><input name="branch" value={editForm.branch || ""} onChange={handleEditChange} /></div>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>Hierarchy IDs</h4>
                    <div className="detail-grid">
                      <div className="detail-item"><label>District ID</label><input name="districtId" value={editForm.districtId || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>Block ID</label><input name="blockId" value={editForm.blockId || ""} onChange={handleEditChange} /></div>
                      <div className="detail-item"><label>GP ID</label><input name="gpId" value={editForm.gpId || ""} onChange={handleEditChange} /></div>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>Upload Images</h4>
                    <div className="images-grid">
                      {["aadhaar", "pan", "passbook", "photo"].map(type => (
                        <div key={type} className="upload-image-item">
                          <label>{type.charAt(0).toUpperCase() + type.slice(1)}</label>
                          <input type="file" accept="image/*" onChange={(e) => handleEditFileChange(type, e.target.files[0])} />
                          {(editPreviews[type] || editForm[`${type}Base64`]) && (
                            <img src={editPreviews[type] || editForm[`${type}Base64`]} alt={type} width="60" style={{ marginTop: "8px", borderRadius: type === "photo" ? "50%" : "8px" }} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="no-data">Failed to load details.</div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn-secondary" onClick={closeModal}>Close</button>
            {/* Only show Edit button if user is ADMIN */}
            {isAdmin && !isEditing && (
              <button className="btn-primary" onClick={() => setIsEditing(true)}>✏️ Edit</button>
            )}
            {isAdmin && isEditing && (
              <>
                <button className="btn-secondary" onClick={() => { setIsEditing(false); setEditForm(memberDetails); setEditPreviews({ aadhaar: null, pan: null, passbook: null, photo: null }); }}>Cancel</button>
                <button className="btn-primary" onClick={saveMember} disabled={modalLoading}>💾 Save</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ==================== RENDER ====================
  if (loading || !currentUser) {
    return (
      <div style={{ display: "flex" }}>
        <Sidebar />
        <div style={{ padding: "40px" }}>Loading teams...</div>
      </div>
    );
  }

  // ==================== STATE COORDINATOR VIEW ====================
  if (currentUser.role === "STATE_COORDINATOR") {
    return (
      <div className="teams-app">
        <Sidebar />
        <div className="teams-main">
          <div className="teams-header">
            <h1>State Coordinator Dashboard</h1>
            <p>Viewing all districts across the state</p>
            <div className="search-bar">
              <input
                type="text"
                placeholder="🔍 Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="teams-container">
            {districts.length === 0 && <div className="no-data">No districts found.</div>}
            {districts.map(district => {
              const districtId = district.districtId;
              const districtName = district.districtName;
              const dCoords = filteredDistrictCoords.filter(c => c.districtId === districtId);
              const blocks = blocksMap[districtId] || [];
              const dKey = `district-${districtId}`;
              const isOpen = expanded[dKey];

              if (searchTerm && dCoords.length === 0) {
                let hasMatch = false;
                for (const block of blocks) {
                  if (filteredBlockCoords.some(c => c.blockId === block.id)) { hasMatch = true; break; }
                  const gps = gpsMap[block.id] || [];
                  if (gps.some(gp => filteredGpCoords.some(c => c.gpId === gp.id))) { hasMatch = true; break; }
                }
                if (!hasMatch) return null;
              }

              return (
                <div key={districtId} className="team-district">
                  <div className="team-district-header" onClick={() => toggleExpand(dKey)}>
                    <span className="toggle-icon">{isOpen ? "▼" : "▶"}</span>
                    <span className="district-title">🏛️ {districtName}</span>
                    <span className="member-count">{dCoords.length} District Coordinator{dCoords.length !== 1 ? "s" : ""}</span>
                  </div>
                  {isOpen && (
                    <div className="team-blocks">
                      {dCoords.length > 0 && (
                        <div className="coordinator-list">
                          <div className="coordinator-label">District Coordinators:</div>
                          {dCoords.map(coord => (
                            <div key={coord.id} className="coordinator-chip" onClick={() => openMemberModal(coord)}>
                              👤 {coord.name} {getPaymentBadge(coord.id)}
                            </div>
                          ))}
                        </div>
                      )}
                      {blocks.map(block => {
                        const bCoords = filteredBlockCoords.filter(c => c.blockId === block.id);
                        const bKey = `${dKey}-block-${block.id}`;
                        const bOpen = expanded[bKey];
                        const gps = gpsMap[block.id] || [];
                        if (searchTerm && bCoords.length === 0 && !gps.some(gp => filteredGpCoords.some(c => c.gpId === gp.id))) return null;
                        return (
                          <div key={block.id} className="team-block">
                            <div className="team-block-header" onClick={() => toggleExpand(bKey)}>
                              <span className="toggle-icon">{bOpen ? "▼" : "▶"}</span>
                              <span className="block-title">📍 {block.name}</span>
                              <span className="member-count">{bCoords.length} Block Coordinator{bCoords.length !== 1 ? "s" : ""}</span>
                            </div>
                            {bOpen && (
                              <div className="team-gps">
                                {bCoords.length > 0 && (
                                  <div className="coordinator-list">
                                    <div className="coordinator-label">Block Coordinators:</div>
                                    {bCoords.map(coord => (
                                      <div key={coord.id} className="coordinator-chip" onClick={() => openMemberModal(coord)}>
                                        👤 {coord.name} {getPaymentBadge(coord.id)}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {gps.map(gp => {
                                  const gCoords = filteredGpCoords.filter(c => c.gpId === gp.id);
                                  const gKey = `${bKey}-gp-${gp.id}`;
                                  const gOpen = expanded[gKey];
                                  if (searchTerm && gCoords.length === 0) return null;
                                  return (
                                    <div key={gp.id} className="team-gp">
                                      <div className="team-gp-header" onClick={() => toggleExpand(gKey)}>
                                        <span className="toggle-icon">{gOpen ? "▼" : "▶"}</span>
                                        <span className="gp-title">🏘️ {gp.name}</span>
                                        <span className="member-count">{gCoords.length} GP Coordinator{gCoords.length !== 1 ? "s" : ""}</span>
                                      </div>
                                      {gOpen && (
                                        <div className="team-members">
                                          {gCoords.map(coord => (
                                            <div key={coord.id} className="member-card" onClick={() => openMemberModal(coord)}>
                                              <div className="member-avatar">👤</div>
                                              <div className="member-info">
                                                <div className="member-name">{coord.name}</div>
                                                <div className="member-role">{coord.role?.replace(/_/g, " ") || "GP Coordinator"}</div>
                                                <div className="member-email">{coord.email}</div>
                                                {getPaymentBadge(coord.id)}
                                              </div>
                                            </div>
                                          ))}
                                          {gCoords.length === 0 && <div className="no-members">No GP coordinators assigned</div>}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {renderModal()}
        {lightboxImage && (
          <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img src={lightboxImage} alt="Full size" />
              <button className="lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==================== ADMIN VIEW ====================
  if (currentUser.role === "ADMIN") {
    return (
      <div className="teams-app">
        <Sidebar />
        <div className="teams-main">
          <div className="teams-header">
            <h1>Teams Overview</h1>
            <p>Hierarchy of coordinators – District → Block → GP</p>
            <div className="search-bar">
              <input
                type="text"
                placeholder="🔍 Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="teams-container">
            {districts.length === 0 && <div className="no-data">No districts found.</div>}
            {districts.map(district => {
              const districtId = district.districtId;
              const districtName = district.districtName;
              const dCoords = filteredDistrictCoords.filter(c => c.districtId === districtId);
              const blocks = blocksMap[districtId] || [];
              const dKey = `district-${districtId}`;
              const isOpen = expanded[dKey];

              if (searchTerm && dCoords.length === 0) {
                let hasMatch = false;
                for (const block of blocks) {
                  if (filteredBlockCoords.some(c => c.blockId === block.id)) { hasMatch = true; break; }
                  const gps = gpsMap[block.id] || [];
                  if (gps.some(gp => filteredGpCoords.some(c => c.gpId === gp.id))) { hasMatch = true; break; }
                }
                if (!hasMatch) return null;
              }

              return (
                <div key={districtId} className="team-district">
                  <div className="team-district-header" onClick={() => toggleExpand(dKey)}>
                    <span className="toggle-icon">{isOpen ? "▼" : "▶"}</span>
                    <span className="district-title">🏛️ {districtName}</span>
                    <span className="member-count">{dCoords.length} District Coordinator{dCoords.length !== 1 ? "s" : ""}</span>
                  </div>
                  {isOpen && (
                    <div className="team-blocks">
                      {dCoords.length > 0 && (
                        <div className="coordinator-list">
                          <div className="coordinator-label">District Coordinators:</div>
                          {dCoords.map(coord => (
                            <div key={coord.id} className="coordinator-chip" onClick={() => openMemberModal(coord)}>
                              👤 {coord.name} {getPaymentBadge(coord.id)}
                            </div>
                          ))}
                        </div>
                      )}
                      {blocks.map(block => {
                        const bCoords = filteredBlockCoords.filter(c => c.blockId === block.id);
                        const bKey = `${dKey}-block-${block.id}`;
                        const bOpen = expanded[bKey];
                        const gps = gpsMap[block.id] || [];
                        if (searchTerm && bCoords.length === 0 && !gps.some(gp => filteredGpCoords.some(c => c.gpId === gp.id))) return null;
                        return (
                          <div key={block.id} className="team-block">
                            <div className="team-block-header" onClick={() => toggleExpand(bKey)}>
                              <span className="toggle-icon">{bOpen ? "▼" : "▶"}</span>
                              <span className="block-title">📍 {block.name}</span>
                              <span className="member-count">{bCoords.length} Block Coordinator{bCoords.length !== 1 ? "s" : ""}</span>
                            </div>
                            {bOpen && (
                              <div className="team-gps">
                                {bCoords.length > 0 && (
                                  <div className="coordinator-list">
                                    <div className="coordinator-label">Block Coordinators:</div>
                                    {bCoords.map(coord => (
                                      <div key={coord.id} className="coordinator-chip" onClick={() => openMemberModal(coord)}>
                                        👤 {coord.name} {getPaymentBadge(coord.id)}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {gps.map(gp => {
                                  const gCoords = filteredGpCoords.filter(c => c.gpId === gp.id);
                                  const gKey = `${bKey}-gp-${gp.id}`;
                                  const gOpen = expanded[gKey];
                                  if (searchTerm && gCoords.length === 0) return null;
                                  return (
                                    <div key={gp.id} className="team-gp">
                                      <div className="team-gp-header" onClick={() => toggleExpand(gKey)}>
                                        <span className="toggle-icon">{gOpen ? "▼" : "▶"}</span>
                                        <span className="gp-title">🏘️ {gp.name}</span>
                                        <span className="member-count">{gCoords.length} GP Coordinator{gCoords.length !== 1 ? "s" : ""}</span>
                                      </div>
                                      {gOpen && (
                                        <div className="team-members">
                                          {gCoords.map(coord => (
                                            <div key={coord.id} className="member-card" onClick={() => openMemberModal(coord)}>
                                              <div className="member-avatar">👤</div>
                                              <div className="member-info">
                                                <div className="member-name">{coord.name}</div>
                                                <div className="member-role">{coord.role?.replace(/_/g, " ") || "GP Coordinator"}</div>
                                                <div className="member-email">{coord.email}</div>
                                                {getPaymentBadge(coord.id)}
                                              </div>
                                            </div>
                                          ))}
                                          {gCoords.length === 0 && <div className="no-members">No GP coordinators assigned</div>}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {renderModal()}
        {lightboxImage && (
          <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img src={lightboxImage} alt="Full size" />
              <button className="lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==================== DISTRICT COORDINATOR VIEW ====================
  if (currentUser.role === "DISTRICT_COORDINATOR") {
    const myDistrict = districts.find(d => d.districtId === currentUser.districtId);
    if (!myDistrict) return <div>District not found</div>;
    const districtBlocks = blocksMap[myDistrict.districtId] || [];
    const dCoords = filteredDistrictCoords.filter(c => c.districtId === myDistrict.districtId);
    const dKey = `district-${myDistrict.districtId}`;

    return (
      <div className="teams-app">
        <Sidebar />
        <div className="teams-main">
          <div className="teams-header">
            <h1>My District: {myDistrict.districtName}</h1>
            <div className="search-bar">
              <input type="text" placeholder="🔍 Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>
          <div className="teams-container">
            <div className="team-district">
              <div className="team-district-header" onClick={() => toggleExpand(dKey)}>
                <span className="toggle-icon">{expanded[dKey] ? "▼" : "▶"}</span>
                <span className="district-title">🏛️ {myDistrict.districtName}</span>
                <span className="member-count">{dCoords.length} DC(s)</span>
              </div>
              {expanded[dKey] && (
                <div className="team-blocks">
                  {dCoords.length > 0 && (
                    <div className="coordinator-list">
                      <div className="coordinator-label">District Coordinators:</div>
                      {dCoords.map(coord => (
                        <div key={coord.id} className="coordinator-chip" onClick={() => openMemberModal(coord)}>
                          👤 {coord.name} {getPaymentBadge(coord.id)}
                        </div>
                      ))}
                    </div>
                  )}
                  {districtBlocks.map(block => {
                    const bCoords = filteredBlockCoords.filter(c => c.blockId === block.id);
                    const bKey = `${dKey}-block-${block.id}`;
                    const gps = gpsMap[block.id] || [];
                    return (
                      <div key={block.id} className="team-block">
                        <div className="team-block-header" onClick={() => toggleExpand(bKey)}>
                          <span className="toggle-icon">{expanded[bKey] ? "▼" : "▶"}</span>
                          <span className="block-title">📍 {block.name}</span>
                          <span className="member-count">{bCoords.length} BC(s)</span>
                        </div>
                        {expanded[bKey] && (
                          <div className="team-gps">
                            {bCoords.length > 0 && (
                              <div className="coordinator-list">
                                {bCoords.map(coord => (
                                  <div key={coord.id} className="coordinator-chip" onClick={() => openMemberModal(coord)}>
                                    👤 {coord.name} {getPaymentBadge(coord.id)}
                                  </div>
                                ))}
                              </div>
                            )}
                            {gps.map(gp => {
                              const gCoords = filteredGpCoords.filter(c => c.gpId === gp.id);
                              const gKey = `${bKey}-gp-${gp.id}`;
                              return (
                                <div key={gp.id} className="team-gp">
                                  <div className="team-gp-header" onClick={() => toggleExpand(gKey)}>
                                    <span className="toggle-icon">{expanded[gKey] ? "▼" : "▶"}</span>
                                    <span className="gp-title">🏘️ {gp.name}</span>
                                    <span className="member-count">{gCoords.length} GPC(s)</span>
                                  </div>
                                  {expanded[gKey] && (
                                    <div className="team-members">
                                      {gCoords.map(coord => (
                                        <div key={coord.id} className="member-card" onClick={() => openMemberModal(coord)}>
                                          <div className="member-avatar">👤</div>
                                          <div className="member-info">
                                            <div className="member-name">{coord.name}</div>
                                            <div className="member-role">{coord.role}</div>
                                            <div className="member-email">{coord.email}</div>
                                            {getPaymentBadge(coord.id)}
                                          </div>
                                        </div>
                                      ))}
                                      {gCoords.length === 0 && <div className="no-members">No GP coordinators</div>}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        {renderModal()}
        {lightboxImage && (
          <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img src={lightboxImage} alt="Full size" />
              <button className="lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==================== BLOCK COORDINATOR VIEW ====================
  if (currentUser.role === "BLOCK_COORDINATOR") {
    let blockName = "Unknown Block";
    for (const blocks of Object.values(blocksMap)) {
      const found = blocks.find(b => b.id === currentUser.blockId);
      if (found) { blockName = found.name; break; }
    }
    const bCoords = filteredBlockCoords.filter(c => c.blockId === currentUser.blockId);
    const gps = gpsMap[currentUser.blockId] || [];
    return (
      <div className="teams-app">
        <Sidebar />
        <div className="teams-main">
          <div className="teams-header">
            <h1>My Block: {blockName}</h1>
            <div className="search-bar">
              <input type="text" placeholder="🔍 Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>
          <div className="teams-container">
            <div className="team-block">
              <div className="team-block-header">
                <span className="block-title">📍 {blockName}</span>
                <span className="member-count">{bCoords.length} BC(s)</span>
              </div>
              <div className="team-gps">
                {bCoords.length > 0 && (
                  <div className="coordinator-list">
                    {bCoords.map(coord => (
                      <div key={coord.id} className="coordinator-chip" onClick={() => openMemberModal(coord)}>
                        👤 {coord.name} {getPaymentBadge(coord.id)}
                      </div>
                    ))}
                  </div>
                )}
                {gps.map(gp => {
                  const gCoords = filteredGpCoords.filter(c => c.gpId === gp.id);
                  const gKey = `gp-${gp.id}`;
                  return (
                    <div key={gp.id} className="team-gp">
                      <div className="team-gp-header" onClick={() => toggleExpand(gKey)}>
                        <span className="toggle-icon">{expanded[gKey] ? "▼" : "▶"}</span>
                        <span className="gp-title">🏘️ {gp.name}</span>
                        <span className="member-count">{gCoords.length} GPC(s)</span>
                      </div>
                      {expanded[gKey] && (
                        <div className="team-members">
                          {gCoords.map(coord => (
                            <div key={coord.id} className="member-card" onClick={() => openMemberModal(coord)}>
                              <div className="member-avatar">👤</div>
                              <div className="member-info">
                                <div className="member-name">{coord.name}</div>
                                <div className="member-role">{coord.role}</div>
                                <div className="member-email">{coord.email}</div>
                                {getPaymentBadge(coord.id)}
                              </div>
                            </div>
                          ))}
                          {gCoords.length === 0 && <div className="no-members">No GP coordinators</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        {renderModal()}
        {lightboxImage && (
          <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img src={lightboxImage} alt="Full size" />
              <button className="lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==================== GP COORDINATOR VIEW ====================
  if (currentUser.role === "GP_COORDINATOR") {
    let gpName = "Unknown GP";
    for (const gps of Object.values(gpsMap)) {
      const found = gps.find(g => g.id === currentUser.gpId);
      if (found) { gpName = found.name; break; }
    }
    const gCoords = filteredGpCoords.filter(c => c.gpId === currentUser.gpId);
    return (
      <div className="teams-app">
        <Sidebar />
        <div className="teams-main">
          <div className="teams-header">
            <h1>My Gram Panchayat: {gpName}</h1>
            <div className="search-bar">
              <input type="text" placeholder="🔍 Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
          </div>
          <div className="teams-container">
            <div className="team-members">
              {gCoords.map(coord => (
                <div key={coord.id} className="member-card" onClick={() => openMemberModal(coord)}>
                  <div className="member-avatar">👤</div>
                  <div className="member-info">
                    <div className="member-name">{coord.name}</div>
                    <div className="member-role">{coord.role}</div>
                    <div className="member-email">{coord.email}</div>
                    {getPaymentBadge(coord.id)}
                  </div>
                </div>
              ))}
              {gCoords.length === 0 && <div className="no-members">No coordinators assigned</div>}
            </div>
          </div>
        </div>
        {renderModal()}
        {lightboxImage && (
          <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              <img src={lightboxImage} alt="Full size" />
              <button className="lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return <div>Role not supported</div>;
}